'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import ChatbotWindow from './ChatbotWindow';

interface FloatingChatLauncherProps {
  className?: string;
}

const FloatingChatLauncher = ({ className = '' }: FloatingChatLauncherProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Detect when footer is in viewport
  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(footer);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleOpenChatbot = () => {
      setIsChatOpen(true);
    };

    window.addEventListener('openChatbot', handleOpenChatbot);
    return () => window.removeEventListener('openChatbot', handleOpenChatbot);
  }, []);

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
  };

  // Don't render button when footer is visible (but keep chat window if open)
  const shouldShowButton = isVisible && !isFooterVisible;

  return (
    <>
      <button data-component-id="src/components/common/FloatingChatLauncher.tsx:59:6" data-component-path="src/components/common/FloatingChatLauncher.tsx" data-component-line="59" data-component-end-line="74" data-component-file="FloatingChatLauncher.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
        onClick={handleChatToggle}
        className={`fixed bottom-6 right-6 lg:bottom-8 lg:right-8 w-14 h-14 lg:w-16 lg:h-16 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-warm-xl hover:shadow-warm-xl transition-all duration-300 hidden lg:flex items-center justify-center z-30 focus-ring ${
          shouldShowButton
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4 pointer-events-none'
        } ${className}`}
        aria-label={isChatOpen ? 'Close chat' : 'Open support chat'}
        title={isChatOpen ? 'Close chat' : 'Need help? Chat with us'}
      >
        {isChatOpen ? (
          <Icon data-component-id="src/components/common/FloatingChatLauncher.tsx:70:10" data-component-path="src/components/common/FloatingChatLauncher.tsx" data-component-line="70" data-component-end-line="70" data-component-file="FloatingChatLauncher.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%2C%22className%22%3A%22lg%3Aw-8%20lg%3Ah-8%22%7D" name="XMarkIcon" size={28} className="lg:w-8 lg:h-8" />
        ) : (
          <Icon data-component-id="src/components/common/FloatingChatLauncher.tsx:72:10" data-component-path="src/components/common/FloatingChatLauncher.tsx" data-component-line="72" data-component-end-line="72" data-component-file="FloatingChatLauncher.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChatBubbleLeftRightIcon%22%2C%22className%22%3A%22lg%3Aw-8%20lg%3Ah-8%22%7D" name="ChatBubbleLeftRightIcon" size={28} className="lg:w-8 lg:h-8" />
        )}
      </button>
      {isChatOpen && <ChatbotWindow data-component-id="src/components/common/FloatingChatLauncher.tsx:75:21" data-component-path="src/components/common/FloatingChatLauncher.tsx" data-component-line="75" data-component-end-line="75" data-component-file="FloatingChatLauncher.tsx" data-component-name="ChatbotWindow" data-component-content="%7B%22elementName%22%3A%22ChatbotWindow%22%7D" onClose={() => setIsChatOpen(false)} />}
    </>
  );
};

export default FloatingChatLauncher;
